import React from "react";
import { Dialog } from "../../ui/Dialog/Dialog";
import { Button } from "../../ui/Button/Button";
import { FileText, Calendar } from "lucide-react";

/**
 * ReleaseNoteModal Component
 * Displays a SAP release note (manual activities, deprecations, etc.) for a release.
 * Content is the raw HTML pasted from the SAP portal, rendered as-is.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.note - Release note object { release, sapNoteNumber, title, releasedOn, content }
 */
export const ReleaseNoteModal = ({ open, onClose, note }) => {
  if (!note) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={note.title}
      maxWidth="3xl"
      footer={
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 border-b pb-3">
          {note.sapNoteNumber && (
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              SAP Note {note.sapNoteNumber}
            </span>
          )}
          {note.releasedOn && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(note.releasedOn).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          )}
        </div>

        <div className="max-h-[65vh] overflow-y-auto pr-2">
          <div
            className="release-note-body text-sm text-gray-700"
            dangerouslySetInnerHTML={{ __html: note.content }}
          />
        </div>
      </div>

      <style>{`
        .release-note-body h3 {
          font-size: 1.05rem;
          font-weight: 700;
          color: #111827;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .release-note-body h3:first-child {
          margin-top: 0;
        }
        .release-note-body p {
          line-height: 1.6;
          margin-bottom: 0.75rem;
        }
        .release-note-body strong {
          font-weight: 700;
          color: #111827;
        }
        .release-note-body em {
          font-style: italic;
        }
        .release-note-body a {
          color: #2563eb;
          text-decoration: underline;
        }
        .release-note-body a:hover {
          color: #1d4ed8;
        }
        .release-note-body ul,
        .release-note-body ol {
          margin: 0.5rem 0 0.75rem 1.25rem;
        }
        .release-note-body ul {
          list-style-type: disc;
        }
        .release-note-body ol {
          list-style-type: decimal;
        }
        .release-note-body ul ul,
        .release-note-body ol ul {
          list-style-type: circle;
        }
        .release-note-body li {
          margin-bottom: 0.25rem;
        }
        .release-note-body .table-responsive {
          overflow-x: auto;
          margin: 0.75rem 0 1rem;
        }
        .release-note-body table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .release-note-body table td,
        .release-note-body table th {
          border: 1px solid #e5e7eb;
          padding: 0.5rem 0.75rem;
          text-align: left;
          vertical-align: top;
        }
        .release-note-body table tr:first-child td {
          font-weight: 700;
          background-color: #f9fafb;
        }
        .release-note-body table tr:nth-child(even) {
          background-color: #fafafa;
        }
      `}</style>
    </Dialog>
  );
};

export default ReleaseNoteModal;
